import { Fragment, useEffect, useState } from "react";
import {
  Accordion, ActionIcon, Alert, Badge, Box, Button, Group, Modal, NumberInput, Paper,
  ScrollArea, Select, Stack, Switch, Table, Tabs, Text, TextInput, Title, Tooltip,
} from "@mantine/core";
import {
  IconAdjustments, IconArrowLeft, IconChevronLeft, IconChevronRight, IconDownload,
  IconInfoCircle, IconRefresh,
} from "@tabler/icons-react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import createApiClient from "../utils/api/apiClient";
import downloadApiFile from "../utils/api/downloadApiFile";
import { useGlobalContext } from "../context/globalContext";
import FeatureSettingsModal, {
  type FeatureSettingsPayload, type FeatureUpdate,
} from "./FeatureSettingsModal";
import FeatureIssueSummaryTables from "./FeatureIssueSummaryTables";
import type { FeatureIssueSummary } from "./featureIssueSummaryUtils";

const api = createApiClient("backtest-audit/");
type Section = "features" | "predictions" | "static_live" | "static_dynamic" | "runs";
type Metric = "bets" | "stakes" | "revenue" | "roi" | "bsp" | "bsp_weighted";
type SplitBy = "all" | "country" | "race_type";
type Status = "green" | "yellow" | "red" | "grey";
type Cell = {
  race_id: string; meeting: string; starts_at: string | null; horse_id: string;
  name: string; static: unknown; dynamic_backtest?: unknown; dynamic_live: unknown;
  status: Status; reason: string; implied_probability_diff_pp?: number | null;
};
type Race = { race_id: string; meeting: string; starts_at: string | null; status: Status; reason: string;
              filtered_out?: boolean;
              static?: number | null; other?: number | null;
              static_metrics?: Figures; other_metrics?: Figures };
type Figures = Record<Metric, number | null>;
type BetDetail = {
  reference: string; stake: number; revenue: number; price: number | null; bsp: number | null;
  placed_at: string | null; predicted_price: number | null; model_value: number | null;
};
type ComparisonPrediction = {
  predicted_price: number | null; model_value: number | null; reference_price: number | null;
};
type Result = {
  static: Figures | null; other: Figures | null; status: Status; paired_races: number;
  universe_races: number; full_static: Figures | null; reason: string; races: Race[];
  control_group_warning?: string; control_group_count?: number | null;
  runners: { horse_id: string; static: Figures; other: Figures; status: Status;
             static_bets: BetDetail[]; comparison_bets: BetDetail[];
             static_prediction: ComparisonPrediction; comparison_prediction: ComparisonPrediction }[];
};
type Row = {
  id?: number; date: string; strategy?: string; model?: string; output?: string;
  split_value?: string | null;
  status: Status; reason: string; snapshot_warning?: string; replay_failed?: boolean; invalid?: boolean;
  control_group_warning?: string; control_group_count?: number | null;
  races?: Race[]; detail?: Cell[] | Result;
  summary?: Pick<Result, "static" | "other" | "paired_races" | "universe_races" | "full_static">;
};
type Comparison = {
  days: string[]; rows: Row[]; aggregates: {
    strategy: string; model: string; output: string; paired_races: number;
    split_value?: string | null;
    shared_runners_pct: number | null; static: Figures; other: Figures;
  }[]; settings: Record<string, unknown>; race_counts: Record<string, number | null>;
  split_by: SplitBy; split_values: string[];
  race_counts_by_split: Record<string, Record<string, number>>;
  feature_issue_summaries: FeatureIssueSummary[];
};
type RunRecord = {
  id: number; date: string; strategy: string; model: string; output: string;
  status: string; reason: string; mlflow_run_id: string; mlflow_run_name: string;
  mlflow_model_version: string; feature_names: string[];
  configuration: Record<string, unknown>; staker: string;
  observations: { at: string; order_ref: string }[];
  runs: { id: number; mode: string; revision: number; status: string; reason: string;
          coverage: Record<string, unknown>; finished_at: string | null }[];
};
type RunsResponse = {
  earliest_date: string;
  days: { date: string; status: string; reason: string; race_count: number }[];
  current_model_run: RunRecord | null;
  strategy_days: RunRecord[];
};
type ModelIdentity = Pick<RunRecord, "strategy" | "model" | "output">;
type RunMode = "STATIC_BACKTEST" | "DYNAMIC_BACKTEST" | "DYNAMIC_LIVE";
type SavedRun = RunRecord["runs"][number];

const titles: Record<Section, string> = {
  features: "Features", predictions: "Predictions", static_live: "Static vs Live",
  static_dynamic: "Static vs Dynamic", runs: "Runs",
};
const settingLabels: Record<string, string> = {
  price_green_pp: "Price green tolerance (pp)",
  price_yellow_pp: "Price yellow tolerance (pp)",
  size_green: "Size green tolerance (absolute)",
  size_yellow: "Size yellow tolerance (absolute)",
};
const sectionSlugs: Record<Section, string> = {
  features: "features", predictions: "predictions", static_live: "static-live",
  static_dynamic: "static-dynamic", runs: "runs",
};
const sectionsBySlug: Record<string, Section> = Object.fromEntries(
  (Object.keys(sectionSlugs) as Section[]).map((section) => [sectionSlugs[section], section])
);
const metrics: Metric[] = ["bets", "stakes", "revenue", "roi", "bsp", "bsp_weighted"];
const metricLabels: Record<Metric, string> = {
  bets: "Bets", stakes: "Stakes (€)", revenue: "Revenue (€)",
  roi: "ROI%", bsp: "BSP%", bsp_weighted: "BSP WGT%",
};
const runModes: RunMode[] = ["STATIC_BACKTEST", "DYNAMIC_BACKTEST", "DYNAMIC_LIVE"];
const runModeLabels: Record<RunMode, string> = {
  STATIC_BACKTEST: "Static backtest", DYNAMIC_BACKTEST: "Dynamic backtest", DYNAMIC_LIVE: "Live",
};
const controlGroupInfo = "Production withholds approximately 20% of races as a control group. These races are excluded from new backtests so Static, Dynamic, and Live use the same race universe.";
const legacyControlGroupWarning = "This run predates the recorded 20% production control-group filter. Its backtests may include races that production intentionally withheld, so the comparison is not like-for-like. Rerun the date to compare only non-control races.";
const splitByOptions: { value: SplitBy; label: string }[] = [
  { value: "all", label: "Show all" },
  { value: "country", label: "By country" },
  { value: "race_type", label: "By race type" },
];

function latestDublinAuditDay(): string {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Dublin", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date());
  const date = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  const today = Date.UTC(Number(date.year), Number(date.month) - 1, Number(date.day));
  return new Date(today - 2 * 86400000).toISOString().slice(0, 10);
}

function daysBefore(end: string, count: number) {
  return new Date(Date.parse(`${end}T00:00:00Z`) - (count - 1) * 86400000).toISOString().slice(0, 10);
}

function shiftDay(day: string, offset: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + offset * 86400000).toISOString().slice(0, 10);
}

function isIsoDate(value: string | null | undefined): value is string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

function StatusBadge({ status, reason, onClick, children }: {
  status: Status; reason?: string; onClick?: () => void; children?: React.ReactNode;
}) {
  const label = reason || (onClick ? "Open detail" : status === "grey" ? "No paired data" : null);
  const badge = <Badge color={status === "grey" ? "gray" : status} variant="filled"
      onClick={onClick} style={{ cursor: onClick || reason ? "pointer" : "default" }}>
      {children || status}
    </Badge>;
  return label ? <Tooltip label={label}>{badge}</Tooltip> : badge;
}

function display(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "number") return Number.isFinite(value) ? value.toFixed(2).replace(/\.?0+$/, "") : "—";
  return String(value);
}

function metricDisplay(metric: Metric, value: unknown): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return display(value);
  if (metric === "stakes" || metric === "revenue") {
    return `${value < 0 ? "-" : ""}€${Math.abs(value).toFixed(2)}`;
  }
  if (metric === "roi" || metric === "bsp" || metric === "bsp_weighted") {
    return `${value.toFixed(2)}%`;
  }
  return String(value);
}

function betCount(value: Figures | null | undefined, metric: Metric): string {
  if (!value || metric === "bets" || typeof value.bets !== "number") return "";
  return ` (${value.bets} bet${value.bets === 1 ? "" : "s"})`;
}

function dublinTime(value: string | null): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Dublin", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    second: "2-digit",
  }).format(new Date(value));
}

function BetDetails({ title, bets }: { title: string; bets: BetDetail[] }) {
  if (!bets.length) return null;
  return <Stack gap={4} style={{ minWidth: 0 }}>
    <Text size="sm" fw={600}>{title}</Text>
    <ScrollArea type="auto" offsetScrollbars>
      <Table withTableBorder withColumnBorders fz="xs" style={{ minWidth: 700 }}>
        <Table.Thead><Table.Tr>
          <Table.Th>Placed</Table.Th><Table.Th>Stake</Table.Th><Table.Th>Price</Table.Th>
          <Table.Th>BSP</Table.Th><Table.Th>Revenue</Table.Th><Table.Th>Predicted price</Table.Th>
          <Table.Th>Model value</Table.Th>
        </Table.Tr></Table.Thead>
        <Table.Tbody>{bets.map((bet) => <Table.Tr key={bet.reference}>
          <Table.Td>{dublinTime(bet.placed_at)}</Table.Td><Table.Td>{display(bet.stake)}</Table.Td>
          <Table.Td>{display(bet.price)}</Table.Td><Table.Td>{display(bet.bsp)}</Table.Td>
          <Table.Td>{display(bet.revenue)}</Table.Td><Table.Td>{display(bet.predicted_price)}</Table.Td>
          <Table.Td>{display(bet.model_value)}</Table.Td>
        </Table.Tr>)}</Table.Tbody>
      </Table>
    </ScrollArea>
  </Stack>;
}

function percentage(value: unknown): string {
  return typeof value === "number" && Number.isFinite(value) ? `${display(value * 100)}%` : "—";
}

function priceDisplay(value: unknown): string {
  return typeof value === "number" && Number.isFinite(value) ? value.toFixed(2) : "—";
}

function ComparisonPredictionCell({ staticValue, comparisonValue, comparisonLabel, modelValue = false }: {
  staticValue: ComparisonPrediction; comparisonValue: ComparisonPrediction;
  comparisonLabel: string; modelValue?: boolean;
}) {
  const formatted = (value: ComparisonPrediction) => modelValue
    ? `${percentage(value.model_value)} (${priceDisplay(value.reference_price)})`
    : priceDisplay(value.predicted_price);
  return <Stack gap={0}>
    <Text size="xs"><Text span fw={600}>Static:</Text> {formatted(staticValue)}</Text>
    <Text size="xs"><Text span fw={600}>{comparisonLabel}:</Text> {formatted(comparisonValue)}</Text>
  </Stack>;
}

function ComparisonMetricCell({ metric, staticValue, comparisonValue }: {
  metric: Metric;
  staticValue: number | null | undefined; comparisonValue: number | null | undefined;
}) {
  return <Box style={{ display: "grid", gridTemplateColumns: "1fr 1fr", minWidth: 130 }}>
    <Text size="sm" ta="center" px="xs">{metricDisplay(metric, staticValue)}</Text>
    <Text size="sm" ta="center" px="xs" style={{ borderLeft: "1px solid var(--mantine-color-gray-3)" }}>
      {metricDisplay(metric, comparisonValue)}
    </Text>
  </Box>;
}

function errorDetail(reason: unknown, fallback: string) {
  if (reason && typeof reason === "object" && "data" in reason &&
      reason.data && typeof reason.data === "object" && "detail" in reason.data) {
    return String(reason.data.detail);
  }
  return fallback;
}

function prediction(value: unknown) {
  if (!value || typeof value !== "object") return "—";
  const row = value as Record<string, unknown>;
  return <Stack gap={0}>
    <Text size="sm">Price {display(row.predicted_price)}</Text>
    <Text size="xs">Probability {percentage(row.predicted_prob)}</Text>
    <Text size="xs">Model {percentage(row.model_value)}</Text>
  </Stack>;
}

function raceExclusions(coverage: Record<string, unknown>): [string, string][] {
  const value = coverage.race_exclusions;
  if (!value || typeof value !== "object" || Array.isArray(value)) return [];
  return Object.entries(value).filter((entry): entry is [string, string] => typeof entry[1] === "string");
}

function strategyLabel(record: ModelIdentity, records: readonly ModelIdentity[]): string {
  const models = new Set(records.filter((item) => item.strategy === record.strategy)
    .map((item) => `${item.model}:${item.output}`));
  return models.size > 1
    ? `${record.strategy} · ${record.model} (${record.output})`
    : record.strategy;
}

function latestRun(runs: SavedRun[], mode: RunMode): SavedRun | undefined {
  return runs.filter((run) => run.mode === mode).sort((left, right) => right.revision - left.revision)[0];
}

function controlGroupDetails(run: SavedRun | undefined): { count: number | null; warning: string } {
  if (!run || run.status !== "complete") return { count: null, warning: "" };
  const filter = run.coverage.control_group_filter;
  if (!filter || typeof filter !== "object" || Array.isArray(filter) ||
      (filter as Record<string, unknown>).version !== 1) {
    return { count: null, warning: legacyControlGroupWarning };
  }
  const races = (filter as Record<string, unknown>).control_races;
  return { count: Array.isArray(races) ? races.length : null, warning: "" };
}

export default function BacktestAudit() {
  const { latestEvent } = useGlobalContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { sectionSlug, day, strategyDayId, raceId: racePathId, meetingName } = useParams();
  const section = sectionsBySlug[sectionSlug || ""] || "features";
  const latestAuditDay = latestDublinAuditDay();
  const selected = strategyDayId && /^\d+$/.test(strategyDayId) && Number(strategyDayId) > 0
    ? Number(strategyDayId) : null;
  const selectedDate = isIsoDate(day) && day <= latestAuditDay ? day : null;
  const raceId = racePathId || null;
  const meeting = meetingName || null;
  const query = new URLSearchParams(location.search);
  const singleDayMode = section !== "runs" && query.get("date_mode") === "day";
  const requestedEnd = query.get("end_date");
  const end = isIsoDate(requestedEnd) && requestedEnd <= latestAuditDay
    ? requestedEnd : selectedDate || latestAuditDay;
  const requestedStart = query.get("start_date");
  const start = singleDayMode
    ? end
    : isIsoDate(requestedStart)
      ? requestedStart <= end ? requestedStart : end
      : daysBefore(end, 7);
  const requestedRunDay = query.get("date");
  const runDay = isIsoDate(requestedRunDay) && requestedRunDay <= latestAuditDay
    ? requestedRunDay : latestAuditDay;
  const showFailedRuns = section === "runs" && query.get("failed") === "1";
  const metric = metrics.includes(query.get("metric") as Metric) ? query.get("metric") as Metric : "bets";
  const requestedSplitBy = query.get("split_by");
  const querySplitBy = (section === "static_live" || section === "static_dynamic") &&
    splitByOptions.some((option) => option.value === requestedSplitBy)
    ? requestedSplitBy as SplitBy : null;
  const querySplitValue = selected && querySplitBy && querySplitBy !== "all"
    ? query.get("split_value") : null;
  const comparisonLabel = section === "static_live" ? "Live" : "Dynamic";
  const basePath = `/backtest-audit/${sectionSlugs[section]}`;
  const dayPath = selected && selectedDate
    ? `${basePath}/days/${selectedDate}/strategy-days/${selected}` : null;
  const canonicalQuery = new URLSearchParams();
  if (section === "runs") {
    if (showFailedRuns) canonicalQuery.set("failed", "1");
    else canonicalQuery.set("date", runDay);
  } else {
    canonicalQuery.set("start_date", start);
    canonicalQuery.set("end_date", end);
    if (singleDayMode) canonicalQuery.set("date_mode", "day");
    if (section === "static_live" || section === "static_dynamic") {
      canonicalQuery.set("metric", metric);
      if (querySplitBy) canonicalQuery.set("split_by", querySplitBy);
      if (querySplitValue) canonicalQuery.set("split_value", querySplitValue);
    }
  }
  const validDetail = !strategyDayId || (section !== "runs" && dayPath &&
    (!meeting || section === "features" || section === "predictions"));
  const canonicalPath = sectionSlugs[section] === sectionSlug && validDetail
    ? location.pathname : dayPath && section !== "runs" ? dayPath : basePath;
  const canonicalSearch = `?${canonicalQuery.toString()}`;
  const urlReady = canonicalPath === location.pathname && canonicalSearch === location.search;
  const [savedComparison, setSavedComparison] = useState<{
    scope: string; selection: string; response: Comparison;
  } | null>(null);
  const [runs, setRuns] = useState<RunsResponse | null>(null);
  const [globalSettings, setGlobalSettings] = useState<Record<string, Record<string, number>>>({});
  const [globalSplitBy, setGlobalSplitBy] = useState<Partial<Record<Section, SplitBy>>>({});
  const [featureSettings, setFeatureSettings] = useState<FeatureSettingsPayload | null>(null);
  const [canManageRuns, setCanManageRuns] = useState(false);
  const [draftSettings, setDraftSettings] = useState<Record<string, number>>({});
  const [draftSplitBy, setDraftSplitBy] = useState<SplitBy>("all");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [rangeBeforeSingle, setRangeBeforeSingle] = useState<{ start: string; end: string } | null>(null);
  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [queueStart, setQueueStart] = useState(runDay);
  const [queueEnd, setQueueEnd] = useState(runDay);
  const [queueError, setQueueError] = useState("");
  const [queueSingleStrategy, setQueueSingleStrategy] = useState(false);
  const [queueStrategies, setQueueStrategies] = useState<RunRecord[]>([]);
  const [queueStrategyId, setQueueStrategyId] = useState<string | null>(null);
  const [loadingQueueStrategies, setLoadingQueueStrategies] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [queueingId, setQueueingId] = useState<number | null>(null);
  const [queueingRange, setQueueingRange] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const splitBy: SplitBy = section === "static_live" || section === "static_dynamic"
    ? querySplitBy ?? (selected ? "all" : globalSplitBy[section] ?? "all")
    : "all";
  const splitValue = selected && splitBy !== "all" ? querySplitValue : null;
  const comparisonScope = JSON.stringify({
    section, start, end, selectedDate, selected, metric, split_by: splitBy, split_value: splitValue,
  });
  const detailSelection = JSON.stringify({ raceId, meeting });
  const comparison = savedComparison?.scope === comparisonScope ? savedComparison.response : null;
  const detailLoaded = savedComparison?.scope === comparisonScope &&
    savedComparison.selection === detailSelection;
  useEffect(() => {
    api.get("settings/").then((data) => {
      setGlobalSettings(data.settings || {}); setCanManageRuns(!!data.can_manage_runs);
      setGlobalSplitBy(data.split_by || {});
      setFeatureSettings(data.feature_settings || null);
    }).catch(() => setError("Unable to load audit settings"));
  }, []);

  useEffect(() => {
    setRuns(null);
  }, [section, runDay, showFailedRuns]);

  useEffect(() => {
    let active = true;
    if (!queueModalOpen || !queueSingleStrategy || !isIsoDate(queueStart)) {
      setQueueStrategies([]);
      setQueueStrategyId(null);
      setLoadingQueueStrategies(false);
      return () => { active = false; };
    }
    setQueueStrategies([]);
    setQueueStrategyId(null);
    setLoadingQueueStrategies(true);
    setQueueError("");
    api.get("runs/", {
      params: { start_date: queueStart, end_date: queueStart },
    }).then((response: RunsResponse) => {
      if (!active) return;
      setQueueStrategies(response.strategy_days.filter(
        (record) => record.status !== "queued" && record.status !== "running"
      ));
    }).catch((reason: unknown) => {
      if (active) setQueueError(errorDetail(reason, "Unable to load strategies for this race day"));
    }).finally(() => {
      if (active) setLoadingQueueStrategies(false);
    });
    return () => { active = false; };
  }, [queueModalOpen, queueSingleStrategy, queueStart]);

  useEffect(() => {
    let active = true;
    if (!urlReady) return;
    setLoading(true);
    setError("");
    const request = section === "runs"
      ? api.get("runs/", { params: showFailedRuns
        ? { failed: "1", include_running: "1" }
        : { start_date: runDay, end_date: runDay, include_running: "1" } })
      : api.post("comparisons/", {
        section, start_date: selectedDate || start, end_date: selectedDate || end, metric,
        strategy_day_id: selected, race_id: raceId, meeting,
        split_by: splitBy, split_value: splitValue,
      });
    request.then((response) => {
      if (!active) return;
      if (section === "runs") setRuns(response);
      else setSavedComparison({ scope: comparisonScope, selection: detailSelection, response });
    }).catch((reason) => {
      if (active) setError(errorDetail(reason, "Unable to load the audit"));
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [urlReady, section, start, end, runDay, showFailedRuns, metric, selected, selectedDate, raceId,
      meeting, splitBy, splitValue, comparisonScope, detailSelection, refresh]);

  useEffect(() => {
    if (latestEvent?.action !== "backtest_audit_rerun_updated" || !latestEvent.date) return;
    const rerunMatchesView = section === "runs"
      ? true
      : selected
        ? latestEvent.strategy_day_id === selected ||
          (!latestEvent.strategy_day_id && latestEvent.date === selectedDate)
        : latestEvent.date >= start && latestEvent.date <= end;
    if (rerunMatchesView) setRefresh((value) => value + 1);
  }, [latestEvent, section, runDay, showFailedRuns, selected, selectedDate, start, end]);

  const modelRows = comparison?.rows.filter((row): row is Row & ModelIdentity & { id: number } =>
    typeof row.id === "number" && !!row.strategy && !!row.model && !!row.output) || [];
  const modelIdentities = Array.from(new Map(modelRows.map((row) =>
    [`${row.strategy}\u0000${row.model}\u0000${row.output}`, row])).values());
  const relevantStrategies = Array.from(new Set(modelRows.map((row) => row.strategy)));
  const selectedRow = modelRows.find((row) => row.id === selected &&
    (splitValue ? row.split_value === splitValue : true));
  const dayRaces = selectedRow?.races?.length ? selectedRow.races
    : selectedRow?.detail && !Array.isArray(selectedRow.detail) ? selectedRow.detail.races : [];
  const meetingNames = Array.from(new Set(dayRaces.map((race) => race.meeting)));
  const selectedDayRaces = [...dayRaces]
    .sort((left, right) => String(left.starts_at).localeCompare(String(right.starts_at)));
  const selectedMeetingRaces = selectedDayRaces.filter((race) => race.meeting === meeting);
  const detailCells = detailLoaded && selectedRow && Array.isArray(selectedRow.detail)
    ? selectedRow.detail : null;
  const comparisonGroups: (string | null)[] = splitBy === "all"
    ? [null] : comparison?.split_values || [];
  const goSection = (next: Section, day?: string) => {
    const nextQuery = new URLSearchParams();
    if (next === "runs") nextQuery.set("date", day || runDay);
    else {
      nextQuery.set("start_date", start);
      nextQuery.set("end_date", end);
      if (singleDayMode) nextQuery.set("date_mode", "day");
      if (next === "static_live" || next === "static_dynamic") nextQuery.set("metric", metric);
    }
    navigate({ pathname: `/backtest-audit/${sectionSlugs[next]}`, search: `?${nextQuery}` });
  };
  const updateQuery = (key: string, value: string) => {
    const nextQuery = new URLSearchParams(location.search);
    nextQuery.set(key, value);
    navigate({ pathname: location.pathname, search: `?${nextQuery}` });
  };
  const showRunsForDay = (date: string) => {
    const nextQuery = new URLSearchParams();
    nextQuery.set("date", date);
    navigate({ pathname: basePath, search: `?${nextQuery}` });
  };
  const toggleFailedRuns = () => {
    const nextQuery = new URLSearchParams();
    if (showFailedRuns) nextQuery.set("date", latestAuditDay);
    else nextQuery.set("failed", "1");
    navigate({ pathname: basePath, search: `?${nextQuery}` });
  };
  const preset = (count: number) => {
    const nextQuery = new URLSearchParams(location.search);
    nextQuery.set("start_date", daysBefore(latestAuditDay, count));
    nextQuery.set("end_date", latestAuditDay);
    nextQuery.delete("date_mode");
    setRangeBeforeSingle(null);
    navigate({ pathname: basePath, search: `?${nextQuery}` });
  };
  const setComparisonDay = (day: string) => {
    if (!isIsoDate(day) || day > latestAuditDay) return;
    const nextQuery = new URLSearchParams(location.search);
    nextQuery.set("start_date", day);
    nextQuery.set("end_date", day);
    nextQuery.set("date_mode", "day");
    navigate({ pathname: basePath, search: `?${nextQuery}` });
  };
  const toggleSingleDay = (enabled: boolean) => {
    const nextQuery = new URLSearchParams(location.search);
    if (enabled) {
      setRangeBeforeSingle({ start, end });
      nextQuery.set("start_date", end);
      nextQuery.set("end_date", end);
      nextQuery.set("date_mode", "day");
    } else {
      const restored = rangeBeforeSingle || { start: daysBefore(end, 7), end };
      nextQuery.set("start_date", restored.start);
      nextQuery.set("end_date", restored.end);
      nextQuery.delete("date_mode");
      setRangeBeforeSingle(null);
    }
    navigate({ pathname: basePath, search: `?${nextQuery}` });
  };
  const openDay = (date: string, id: number, group: string | null) => {
    const nextQuery = new URLSearchParams(location.search);
    if (splitBy !== "all" && group) {
      nextQuery.set("split_by", splitBy);
      nextQuery.set("split_value", group);
    } else {
      nextQuery.delete("split_by");
      nextQuery.delete("split_value");
    }
    navigate({ pathname: `${basePath}/days/${date}/strategy-days/${id}`, search: `?${nextQuery}` });
  };
  const showAllModels = () => {
    const nextQuery = new URLSearchParams(location.search);
    nextQuery.delete("split_value");
    navigate({ pathname: basePath, search: `?${nextQuery}` });
  };
  const openRace = (id: string) => {
    if (dayPath) navigate({ pathname: `${dayPath}/races/${encodeURIComponent(id)}`, search: location.search });
  };
  const openMeeting = (name: string) => {
    if (dayPath) navigate({ pathname: `${dayPath}/meetings/${encodeURIComponent(name)}`, search: location.search });
  };
  const openSettings = () => {
    setDraftSettings({ ...globalSettings[section] });
    setDraftSplitBy(splitBy);
    setSettingsOpen(true);
  };
  const editDraftSetting = (key: string, value: number | string) => {
    if (typeof value !== "number") return;
    setDraftSettings((current) => ({ ...current, [key]: value }));
  };
  let settingsError: string | null = null;
  if (section === "predictions" &&
      draftSettings.price_green_pp > draftSettings.price_yellow_pp) {
    settingsError = "Green price tolerance cannot exceed yellow tolerance";
  } else if ((section === "static_live" || section === "static_dynamic") &&
      draftSettings.rate_green_pp > draftSettings.rate_yellow_pp) {
    settingsError = "Green rate tolerance cannot exceed yellow tolerance";
  };
  const saveGlobal = async () => {
    if (settingsError || section === "features") return;
    try {
      const response = await api.patch("settings/", { section, values: {
        ...draftSettings,
        ...((section === "static_live" || section === "static_dynamic")
          ? { split_by: draftSplitBy } : {}),
      } });
      setGlobalSettings((current) => ({ ...current, [section]: response.values }));
      if (section === "static_live" || section === "static_dynamic") {
        setGlobalSplitBy((current) => ({ ...current, [section]: draftSplitBy }));
        if (selected || querySplitBy) {
          const nextQuery = new URLSearchParams(location.search);
          nextQuery.delete("split_value");
          if (draftSplitBy === "all") nextQuery.delete("split_by");
          else nextQuery.set("split_by", draftSplitBy);
          navigate({ pathname: selected ? basePath : location.pathname, search: `?${nextQuery}` });
        }
      }
      setSettingsOpen(false);
      setRefresh((value) => value + 1);
    } catch (reason: unknown) { setError(errorDetail(reason, "Unable to save settings")); }
  };
  const saveFeatureSettings = async (
    strategy: string, updates: FeatureUpdate[], alertsEnabled: boolean
  ) => {
    try {
      const response = await api.patch("settings/", {
        section: "features", strategy, updates, alerts_enabled: alertsEnabled,
      });
      setFeatureSettings(response.feature_settings);
      setRefresh((value) => value + 1);
    } catch (reason: unknown) {
      const message = errorDetail(reason, "Unable to save feature settings");
      setError(message);
      throw new Error(message);
    }
  };
  const rerun = async (id: number) => {
    setQueueingId(id);
    setError("");
    try {
      const response = await api.post(`strategy-days/${id}/rerun/`);
      setRuns((current) => current && ({ ...current, strategy_days: current.strategy_days.map((record) =>
        record.id === id ? { ...record, status: response.status, reason: "" } : record),
      }));
      setRefresh((value) => value + 1);
    } catch (reason: unknown) { setError(errorDetail(reason, "Unable to queue replay")); }
    finally { setQueueingId(null); }
  };
  const openQueueModal = () => {
    setQueueStart(runDay);
    setQueueEnd(runDay);
    setQueueError("");
    setQueueSingleStrategy(false);
    setQueueStrategies([]);
    setQueueStrategyId(null);
    setQueueModalOpen(true);
  };
  const queueRuns = async () => {
    if (queueSingleStrategy && !queueStrategyId) {
      setQueueError("Choose a strategy day to rerun");
      return;
    }
    if (!queueSingleStrategy && queueEnd < queueStart) {
      setQueueError("The end date must be on or after the start date");
      return;
    }
    setQueueingRange(true);
    setQueueError("");
    try {
      if (queueSingleStrategy) {
        await api.post(`strategy-days/${queueStrategyId}/rerun/`);
      } else {
        await api.post("runs/", { start_date: queueStart, end_date: queueEnd });
      }
      setQueueModalOpen(false);
      setRefresh((value) => value + 1);
    } catch (reason: unknown) {
      setQueueError(errorDetail(reason,
        queueSingleStrategy ? "Unable to queue strategy day" : "Unable to queue date range"));
    } finally { setQueueingRange(false); }
  };
  const refreshAudit = async () => {
    setRefreshing(true);
    setError("");
    try {
      await api.post("refresh/");
      setRefresh((value) => value + 1);
    } catch (reason: unknown) {
      setError(errorDetail(reason, "Unable to refresh the audit"));
    } finally {
      setRefreshing(false);
    }
  };
  const exportDetails = async () => {
    setExporting(true);
    setError("");
    try {
      const file = await downloadApiFile("backtest-audit/", "export/", {
        section, start_date: start, end_date: end,
      });
      const url = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = `backtest-audit-${section}-${start}-to-${end}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (reason: unknown) {
      setError(errorDetail(reason,
        section === "static_live" || section === "static_dynamic"
          ? "Unable to export bets" : "Unable to export audit details"));
    } finally {
      setExporting(false);
    }
  };

  if (!urlReady) return <Navigate to={{ pathname: canonicalPath, search: canonicalSearch }} replace />;

  return <Stack gap="md">
    <Group justify="space-between">
      <div><Title order={2}>Backtest Audit</Title><Text c="dimmed" size="sm">Race dates and times use Europe/Dublin.</Text></div>
      <Group gap="xs">
        {section !== "runs" && <Button variant="light" leftSection={<IconAdjustments size={16} />}
          onClick={openSettings}>Settings</Button>}
        <Button variant="light" leftSection={<IconRefresh size={16} />}
          onClick={refreshAudit} loading={refreshing}>Refresh</Button>
      </Group>
    </Group>
    <Tabs value={section} onChange={(value) => goSection(value as Section)}>
      <Tabs.List>{(Object.keys(titles) as Section[]).map((key) => <Tabs.Tab key={key} value={key}>{titles[key]}</Tabs.Tab>)}</Tabs.List>
    </Tabs>
    {!selected && <Paper withBorder p="md">
      {section === "runs" ? <Group align="end" justify="space-between" gap="sm">
        <Group align="end" gap="sm">
          <TextInput label="Race day" type="date" value={runDay} min="2026-08-19" max={latestAuditDay}
            onChange={(event) => showRunsForDay(event.currentTarget.value)} />
          <Text size="sm" c="dimmed">Available from {runs?.earliest_date || "2026-08-19"}.</Text>
        </Group>
        <Button variant="subtle" color="red" onClick={toggleFailedRuns}>
          {showFailedRuns ? "See Latest" : "Show Failed Runs"}
        </Button>
      </Group> : <Group align="end" justify="space-between" gap="sm">
        <Group align="end" gap="sm">
          {[14, 7, 3, 1].map((count) => <Button key={count} size="xs" variant={!singleDayMode && start === daysBefore(latestAuditDay, count) && end === latestAuditDay ? "filled" : "light"}
            onClick={() => preset(count)}>{count} day{count === 1 ? "" : "s"}</Button>)}
          {singleDayMode ? <>
            <TextInput label="Race day" type="date" value={end} max={latestAuditDay}
              onChange={(event) => setComparisonDay(event.currentTarget.value)} />
            <ActionIcon variant="light" size="lg" aria-label="Previous race day"
              onClick={() => setComparisonDay(shiftDay(end, -1))}>
              <IconChevronLeft size={18} />
            </ActionIcon>
            <ActionIcon variant="light" size="lg" aria-label="Next race day"
              disabled={end >= latestAuditDay}
              onClick={() => setComparisonDay(shiftDay(end, 1))}>
              <IconChevronRight size={18} />
            </ActionIcon>
            <Switch label="1 day" checked onChange={(event) =>
              toggleSingleDay(event.currentTarget.checked)} />
          </> : <>
            <TextInput label="From" type="date" value={start} max={end}
              onChange={(event) => updateQuery("start_date", event.currentTarget.value)} />
            <TextInput label="To" type="date" value={end} max={latestAuditDay}
              onChange={(event) => updateQuery("end_date", event.currentTarget.value)} />
            <Switch label="1 day" checked={false} onChange={(event) =>
              toggleSingleDay(event.currentTarget.checked)} />
          </>}
        </Group>
        <Button
          leftSection={<IconDownload size={16} />} onClick={exportDetails} loading={exporting}>
          {section === "static_live" || section === "static_dynamic"
            ? "Export Bets to CSV" : "Export Details to CSV"}
        </Button>
      </Group>}
    </Paper>}
    {error && <Alert color="red">{error}</Alert>}
    {loading && <Text c="dimmed">Loading audit data…</Text>}
    {section === "runs" && runs && <Stack>
      {runs.current_model_run && <Alert color="blue" title="Current model run">
        <Group justify="space-between" align="center">
          <div><Text fw={600}>{runs.current_model_run.date} · {runs.current_model_run.strategy}
            {` · ${runs.current_model_run.model} (${runs.current_model_run.output})`}</Text>
            <Text size="xs" c="dimmed">MLflow {runs.current_model_run.mlflow_run_name || "unmatched"}
              {` · version ${runs.current_model_run.mlflow_model_version || "?"}`}</Text></div>
          {(showFailedRuns || runs.current_model_run.date !== runDay) && <Button size="xs" variant="light"
            onClick={() => showRunsForDay(runs.current_model_run!.date)}>View race day</Button>}
        </Group>
      </Alert>}
      <Group justify="space-between"><Title order={4}>{showFailedRuns ? "Failed Runs" : `Runs for ${runDay}`}</Title>
        <Group gap="sm"><Text size="sm" c="dimmed">{runs.strategy_days.length} strategy model{runs.strategy_days.length === 1 ? "" : "s"}</Text>
          {canManageRuns && <Button onClick={openQueueModal}>Select Date Range to Queue Runs</Button>}
        </Group></Group>
      {!showFailedRuns && runs.strategy_days.length === 0 && runs.days.filter((day) => day.status !== "scanned").map((day) => <Alert key={day.date} color="gray">
        {day.date}: {day.reason || day.status} ({day.race_count} races)
      </Alert>)}
      {runs.strategy_days.length === 0 && <Alert color="gray">
        {showFailedRuns ? "No failed model runs." : "No model runs saved for this race day."}
      </Alert>}
      {runs.strategy_days.map((record) => <Paper key={record.id} withBorder p="md">
        <Group justify="space-between"><Text fw={600}>
          {showFailedRuns && `${record.date} · `}{strategyLabel(record, runs.strategy_days)}
        </Text>
          <Group><StatusBadge status={record.status === "complete" ? "green" : record.status === "failed" ? "red" : "grey"} reason={record.reason}>{record.status}</StatusBadge>
            {canManageRuns && <Button size="xs" variant="light" onClick={() => rerun(record.id)}
              loading={queueingId === record.id}
              disabled={record.status === "queued" || record.status === "running"}>Queue rerun</Button>}</Group></Group>
        {record.reason && record.status === "failed" && <Text c="red" size="sm">{record.reason}</Text>}
        <Text size="xs" c="dimmed" mt="xs">MLflow {record.mlflow_run_name || "unmatched"} · version {record.mlflow_model_version || "?"} · {record.mlflow_run_id || "no run ID"} · Staker {record.staker || "unknown"}</Text>
        <ScrollArea mt="md"><Group wrap="nowrap" align="stretch" gap="sm" style={{ minWidth: 720 }}>
          {runModes.map((mode) => {
            const run = latestRun(record.runs, mode);
            const color = run?.status === "complete" ? "green" : run?.status === "failed" ? "red" : "gray";
            const exclusions = run ? raceExclusions(run.coverage) : [];
            const controlGroup = mode === "DYNAMIC_LIVE"
              ? controlGroupDetails(run) : { count: null, warning: "" };
            return <Paper key={mode} withBorder radius="md" p="sm" style={{ flex: 1, minWidth: 220,
              borderTop: `3px solid var(--mantine-color-${color}-6)` }}>
              <Group justify="space-between" gap="xs"><Text size="sm" fw={600}>{runModeLabels[mode]}</Text>
                <Badge size="sm" color={color} variant="light">{run?.status || "No run"}</Badge></Group>
              {run && <Text size="xs" c="dimmed" mt="xs">Revision {run.revision}
                {typeof run.coverage.universe === "number" && Array.isArray(run.coverage.covered_races)
                  ? ` · ${run.coverage.covered_races.length}/${run.coverage.universe} races` : ""}</Text>}
              {run && mode === "DYNAMIC_LIVE" && <Group gap={4} mt={2}>
                <Text size="xs" c="dimmed">Control group: {controlGroup.count ?? "not recorded"}</Text>
                <Tooltip label={controlGroup.warning || controlGroupInfo} multiline maw={360}>
                  <IconInfoCircle size={14} color="var(--mantine-color-gray-6)" />
                </Tooltip>
              </Group>}
              {run?.reason && <Text size="xs" c="red" mt="xs">{run.reason}</Text>}
              {exclusions.length > 0 && <details><summary>{exclusions.length} excluded race{exclusions.length === 1 ? "" : "s"}</summary>
                {exclusions.map(([race, reason]) => <Text key={race} size="xs" c="dimmed">{race}: {reason}</Text>)}
              </details>}
            </Paper>;
          })}
        </Group></ScrollArea>
        <details><summary>Evidence and run history</summary>
          <Box component="pre" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
            {JSON.stringify({ configuration: record.configuration, features: record.feature_names,
              observations: record.observations, runs: record.runs }, null, 2)}
          </Box></details>
      </Paper>)}
    </Stack>}
    {section !== "runs" && comparison && <Stack>
      {!selected && comparisonGroups.length === 0 && <Alert color="gray">
        No races have the metadata required for this split.
      </Alert>}
      {!selected && (section === "static_live" || section === "static_dynamic") &&
        <Select maw={260} label="Metric displayed in the strategy grids" value={metric}
          allowDeselect={false} onChange={(value) => { if (value) updateQuery("metric", value); }}
          data={metrics.map((value) => ({ value, label: metricLabels[value] }))} />}
      {!selected && comparisonGroups.map((group) => <Fragment key={group || "all"}>
        {(section === "static_live" || section === "static_dynamic") && <Paper withBorder p="md">
          <Title order={4}>Paired race aggregates{group ? ` · ${group}` : ""}</Title>
          <ScrollArea><Table striped highlightOnHover><Table.Thead><Table.Tr><Table.Th>Strategy</Table.Th><Table.Th>Paired races</Table.Th>
            <Table.Th><Tooltip label="Distinct bet runners selected by both models, divided by those selected by either model">
              <Text span inherit>Shared Runners</Text></Tooltip></Table.Th>
            {metrics.map((key) => <Table.Th key={key}>{metricLabels[key]}<Text size="xs">Static / {comparisonLabel}</Text></Table.Th>)}
          </Table.Tr></Table.Thead><Table.Tbody>{comparison.aggregates
            .filter((row) => (row.split_value || null) === group)
            .map((row) => <Table.Tr key={`${row.strategy}-${row.model}-${row.output}`}>
            <Table.Td>{strategyLabel(row, modelIdentities)}</Table.Td><Table.Td>{row.paired_races}</Table.Td>
            <Table.Td>{typeof row.shared_runners_pct === "number"
              ? `${Math.round(row.shared_runners_pct)}%` : "—"}</Table.Td>
            {metrics.map((key) => <Table.Td key={key}>{metricDisplay(key, row.static[key])} / {metricDisplay(key, row.other[key])}</Table.Td>)}
          </Table.Tr>)}</Table.Tbody></Table></ScrollArea>
        </Paper>}
        <Paper withBorder p="md">
          <Title order={4}>Strategies by race day{group ? ` · ${group}` : ""}</Title>
          <ScrollArea><Table striped><Table.Thead><Table.Tr><Table.Th>Strategy</Table.Th>
            {comparison.days.map((day) => <Table.Th key={day}>{day.slice(5)}</Table.Th>)}
          </Table.Tr></Table.Thead><Table.Tbody>{(modelIdentities.length ? modelIdentities : [null]).map((identity) =>
            <Table.Tr key={identity ? `${identity.strategy}-${identity.model}-${identity.output}` : "no-model"}>
              <Table.Td style={{ minWidth: 220 }}>{identity ? strategyLabel(identity, modelIdentities) : "No model evidence"}</Table.Td>
              {comparison.days.map((day) => {
                const row = identity ? comparison.rows.find((entry) => entry.strategy === identity.strategy
                  && entry.model === identity.model && entry.output === identity.output && entry.date === day
                  && (entry.split_value || null) === group) : undefined;
                const generic = comparison.rows.find((entry) => !entry.id && entry.date === day);
                return <Table.Td key={day}><Group gap={4} wrap="nowrap"><StatusBadge
                  status={row?.status || "grey"}
                  reason={row ? row.reason : generic?.reason || "No model evidence"}
                  onClick={row?.replay_failed ? () => goSection("runs", day) :
                    row && row.status !== "grey" && row.id ? () => openDay(day, row.id!, group) : undefined}>
                    {row?.replay_failed ? "Model failed to run" : row?.invalid ? "Invalid" : undefined}
                  </StatusBadge>
                  {row?.control_group_warning && <Tooltip label={row.control_group_warning} multiline maw={360}>
                    <IconInfoCircle size={14} color="var(--mantine-color-gray-6)" />
                  </Tooltip>}
                </Group>
                  {row?.summary && <Text size="xs">{metricLabels[metric]}: Static {metricDisplay(metric, row.summary.static?.[metric])} · {comparisonLabel} {metricDisplay(metric, row.summary.other?.[metric])}</Text>}
                  {row?.summary && <Text size="xs" c="dimmed">{row.summary.paired_races}/{row.summary.universe_races} races</Text>}
                </Table.Td>;
              })}</Table.Tr>)}
            <Table.Tr style={{ borderTop: "2px solid var(--mantine-color-gray-3)" }}>
              <Table.Td><Text fw={600}>Race count</Text></Table.Td>
              {comparison.days.map((day) => {
                const dayRows = comparison.rows.filter((row) => row.id && row.date === day
                  && (row.split_value || null) === group);
                const counts = Array.from(new Set(dayRows
                  .map((row) => row.control_group_count)
                  .filter((value): value is number => typeof value === "number"))).sort((a, b) => a - b);
                const countLabel = counts.length > 1
                  ? `${counts[0]}–${counts[counts.length - 1]}`
                  : counts[0] ?? "not recorded";
                const warning = dayRows.find((row) => row.control_group_warning)?.control_group_warning;
                return <Table.Td key={day}>
                  <Text fw={600}>{display(group
                    ? comparison.race_counts_by_split?.[group]?.[day]
                    : comparison.race_counts?.[day])}</Text>
                  <Group gap={4} wrap="nowrap">
                    <Text size="xs" c="dimmed">Control group: {countLabel}</Text>
                    <Tooltip label={warning || controlGroupInfo} multiline maw={360}>
                      <IconInfoCircle size={13} color="var(--mantine-color-gray-6)" />
                    </Tooltip>
                  </Group>
                </Table.Td>;
              })}
            </Table.Tr>
          </Table.Tbody></Table></ScrollArea>
        </Paper>
        {section === "features" && <FeatureIssueSummaryTables
          days={comparison.days}
          summaries={comparison.feature_issue_summaries || []}
        />}
      </Fragment>)}
      {selected && !selectedRow && !loading && <Alert color="gray">No strategy run was found for this day.</Alert>}
      {selected && selectedRow && <Paper withBorder p="md"><Group mb="sm"><Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />}
        onClick={showAllModels}>All models</Button>
        <Title order={4}>{selectedRow ? strategyLabel(selectedRow, modelIdentities) : "Strategy"} · {selectedRow?.date}
          {selectedRow.split_value ? ` · ${selectedRow.split_value}` : ""}</Title></Group>
        <Stack gap="xs">
          {selectedRow.snapshot_warning && <Alert color="orange">{selectedRow.snapshot_warning}</Alert>}
          {selectedRow.control_group_warning && <Alert color="orange">
            {selectedRow.control_group_warning}
          </Alert>}
          <Group justify="space-between" align="end"><Text fw={600}>Races for the day</Text>
            <Group align="end">
              {(section === "static_live" || section === "static_dynamic") && <Select maw={240}
                label="Metric displayed" value={metric} allowDeselect={false}
                onChange={(value) => { if (value) updateQuery("metric", value); }}
                data={metrics.map((value) => ({ value, label: metricLabels[value] }))} />}
              {(raceId || meeting) && <Button size="xs" variant="subtle" onClick={() => {
                if (dayPath) navigate({ pathname: dayPath, search: location.search });
              }}>All races for the day</Button>}
            </Group>
          </Group>
          {meetingNames.map((meetingLabel) => {
            const meetingRaces = dayRaces.filter((race) => race.meeting === meetingLabel)
              .sort((a, b) => String(a.starts_at).localeCompare(String(b.starts_at)));
            return <ScrollArea key={meetingLabel} type="auto" offsetScrollbars>
              <Group align="center" wrap="nowrap" style={{ width: "max-content",
                opacity: meetingRaces.every((race) => race.filtered_out) ? 0.45 : 1 }}>
                <Text fw={500} c={meetingRaces.every((race) => race.filtered_out) ? "dimmed" : undefined}
                  style={{ minWidth: 160, whiteSpace: "nowrap" }}>{meetingLabel}</Text>
                {(section === "features" || section === "predictions") && meetingLabel && <Button size="xs"
                  variant={meeting === meetingLabel ? "filled" : "light"}
                  onClick={() => openMeeting(meetingLabel)}>View all races</Button>}
                {meetingRaces.map((race, index) => <Box key={race.race_id}
                  style={{ opacity: race.filtered_out ? 0.45 : 1 }}><StatusBadge status={race.status} reason={race.reason}
                  onClick={race.status !== "grey" ? () => openRace(race.race_id) : undefined}>
                  R{index + 1} · {race.starts_at ? new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Dublin", hour: "2-digit", minute: "2-digit" }).format(new Date(race.starts_at)) : race.race_id}
                </StatusBadge>
                  {(section === "static_live" || section === "static_dynamic") && <Stack gap={0} mt={2}>
                    <Text size="xs" fw={600}>{metricLabels[metric]}</Text>
                    <Text size="xs">Static {metricDisplay(metric, race.static)}{betCount(race.static_metrics, metric)}</Text>
                    <Text size="xs">{comparisonLabel} {metricDisplay(metric, race.other)}{betCount(race.other_metrics, metric)}</Text>
                  </Stack>}
                </Box>)}
              </Group>
            </ScrollArea>;
          })}
          {detailLoaded && !raceId && !meeting && !Array.isArray(selectedRow.detail) && selectedRow.detail && <Group>
            <Text size="sm">Paired races: {selectedRow.detail.paired_races}/{selectedRow.detail.universe_races} · Static full day: {metricDisplay(metric, selectedRow.detail.full_static?.[metric])} · Paired static: {metricDisplay(metric, selectedRow.detail.static?.[metric])} · Paired {comparisonLabel.toLowerCase()}: {metricDisplay(metric, selectedRow.detail.other?.[metric])}</Text>
            <Group gap={4} wrap="nowrap"><Text size="sm" c="dimmed">
              Control group: {selectedRow.detail.control_group_count ?? "not recorded"}
            </Text><Tooltip label={selectedRow.detail.control_group_warning || controlGroupInfo}
              multiline maw={360}><IconInfoCircle size={14}
                color="var(--mantine-color-gray-6)" /></Tooltip></Group>
            <Button size="xs" variant="subtle" onClick={() => goSection("runs", selectedRow.date)}>View saved runs</Button>
          </Group>}
        </Stack>
        {!raceId && detailCells && <Stack mt="md">
          <Title order={5}>{meeting ? `${meeting} · all races` : "All races for the day"}</Title>
          {(meeting ? selectedMeetingRaces : selectedDayRaces).length === 0
            ? <Alert color="gray">No races were found for this {meeting ? "meeting" : "day"}.</Alert>
            : <RaceDetailAccordion
                races={meeting ? selectedMeetingRaces : selectedDayRaces}
                cells={detailCells}
                section={section}
                showMeeting={!meeting}
              />}
        </Stack>}
        {raceId && detailLoaded && <Stack mt="md">{section !== "features" && <Title order={5}>Race {raceId}</Title>}
          {Array.isArray(selectedRow.detail) ? section === "features" ? <FeatureTable raceLabel={`Race ${raceId}`} cells={selectedRow.detail.filter((cell) => cell.race_id === raceId)} />
            : <PredictionTable cells={selectedRow.detail.filter((cell) => cell.race_id === raceId)} />
            : <Stack><Text>Static {metricLabels[metric]}: {metricDisplay(metric, selectedRow.detail?.static?.[metric])}{betCount(selectedRow.detail?.static, metric)} · {comparisonLabel} {metricLabels[metric]}: {metricDisplay(metric, selectedRow.detail?.other?.[metric])}{betCount(selectedRow.detail?.other, metric)} · {selectedRow.detail?.paired_races} paired race</Text>
                <Table striped><Table.Thead><Table.Tr><Table.Th>Runner</Table.Th><Table.Th>Static {metric}</Table.Th><Table.Th>{comparisonLabel} {metric}</Table.Th><Table.Th>Status</Table.Th><Table.Th>Predicted price</Table.Th><Table.Th>Model value (reference price)</Table.Th></Table.Tr></Table.Thead>
                  <Table.Tbody>{selectedRow.detail?.runners.map((runner) => <Fragment key={runner.horse_id}>
                    <Table.Tr><Table.Td>{runner.horse_id}</Table.Td><Table.Td>{metricDisplay(metric, runner.static[metric])}</Table.Td>
                      <Table.Td>{metricDisplay(metric, runner.other[metric])}</Table.Td><Table.Td><StatusBadge status={runner.status} /></Table.Td>
                      <Table.Td><ComparisonPredictionCell staticValue={runner.static_prediction}
                        comparisonValue={runner.comparison_prediction} comparisonLabel={comparisonLabel} /></Table.Td>
                      <Table.Td><ComparisonPredictionCell staticValue={runner.static_prediction}
                        comparisonValue={runner.comparison_prediction} comparisonLabel={comparisonLabel} modelValue /></Table.Td>
                    </Table.Tr>
                    {(runner.static_bets.length > 0 || runner.comparison_bets.length > 0) && <Table.Tr>
                      <Table.Td colSpan={6}><Stack gap="md">
                        <BetDetails title="Static bets" bets={runner.static_bets} />
                        <BetDetails title={`${comparisonLabel} bets`} bets={runner.comparison_bets} />
                      </Stack></Table.Td>
                    </Table.Tr>}
                  </Fragment>)}</Table.Tbody></Table>
              </Stack>}
        </Stack>}
        {(section === "static_live" || section === "static_dynamic") && dayRaces.length > 0 &&
          <Stack mt="xl" gap="xs"><Title order={5}>All race metrics</Title>
            <Text size="sm" c="dimmed">Each metric shows Static | {comparisonLabel}.</Text>
            <ScrollArea type="auto" offsetScrollbars><Table striped withTableBorder withColumnBorders>
              <Table.Thead><Table.Tr><Table.Th>Race</Table.Th>
                {metrics.map((key) => <Table.Th key={key} style={{ minWidth: 150 }}>
                  {metricLabels[key]}<Text size="xs" c="dimmed">Static | {comparisonLabel}</Text>
                </Table.Th>)}
              </Table.Tr></Table.Thead>
              <Table.Tbody>{dayRaces.map((race) => <Table.Tr key={race.race_id}
                style={{ opacity: race.filtered_out ? 0.45 : 1 }}>
                <Table.Td><Button variant="subtle" size="compact-sm"
                  disabled={race.status === "grey"} onClick={() => openRace(race.race_id)}>
                  {race.race_id}
                </Button></Table.Td>
                {metrics.map((key) => <Table.Td key={key} p={0}>
                  <ComparisonMetricCell metric={key} staticValue={race.static_metrics?.[key]}
                    comparisonValue={race.other_metrics?.[key]} />
                </Table.Td>)}
              </Table.Tr>)}</Table.Tbody>
            </Table></ScrollArea>
          </Stack>}
      </Paper>}
    </Stack>}
    <Modal opened={queueModalOpen} onClose={() => setQueueModalOpen(false)}
      title="Queue audit runs">
      <Stack>
        <Text size="sm" c="dimmed">
          {queueSingleStrategy
            ? "Choose one race day, then select the saved strategy day to rerun."
            : "Every race day in this inclusive range will be queued."}
          {` Available from ${runs?.earliest_date || "2026-08-19"}.`}
        </Text>
        <Switch label="Do 1 strategy day" checked={queueSingleStrategy}
          onChange={(event) => {
            const checked = event.currentTarget.checked;
            setQueueSingleStrategy(checked);
            setQueueEnd(queueStart);
            setQueueError("");
          }} />
        {queueSingleStrategy ? <>
          <TextInput label="Race day" type="date" value={queueStart}
            min={runs?.earliest_date || "2026-08-19"} max={latestAuditDay}
            onChange={(event) => { setQueueStart(event.currentTarget.value); setQueueError(""); }} />
          <Select label="Strategy day" placeholder={loadingQueueStrategies
              ? "Loading strategies…" : "Choose a strategy"}
            data={queueStrategies.map((record) => ({
              value: String(record.id),
              label: `${strategyLabel(record, queueStrategies)} · ${record.status}`,
            }))}
            value={queueStrategyId} onChange={(value) => {
              setQueueStrategyId(value); setQueueError("");
            }} disabled={loadingQueueStrategies || queueStrategies.length === 0}
            searchable allowDeselect={false} />
          {!loadingQueueStrategies && queueStrategies.length === 0 &&
            <Alert color="gray">No saved strategy days are available for this race day. Queue the full day first.</Alert>}
        </> : <Group grow align="start">
          <TextInput label="From" type="date" value={queueStart}
            min={runs?.earliest_date || "2026-08-19"} max={queueEnd || latestAuditDay}
            onChange={(event) => { setQueueStart(event.currentTarget.value); setQueueError(""); }} />
          <TextInput label="To" type="date" value={queueEnd}
            min={queueStart || runs?.earliest_date || "2026-08-19"} max={latestAuditDay}
            onChange={(event) => { setQueueEnd(event.currentTarget.value); setQueueError(""); }} />
        </Group>}
        {queueError && <Alert color="red" title="Cannot queue runs">{queueError}</Alert>}
        <Group justify="flex-end">
          <Button variant="light" onClick={() => setQueueModalOpen(false)}>Cancel</Button>
          <Button onClick={queueRuns} loading={queueingRange}
            disabled={!isIsoDate(queueStart) || loadingQueueStrategies ||
              (queueSingleStrategy ? !queueStrategyId : !isIsoDate(queueEnd))}>
            {queueSingleStrategy ? "Queue Strategy" : "Queue Runs"}
          </Button>
        </Group>
      </Stack>
    </Modal>
    {section === "features" && featureSettings && settingsOpen && <FeatureSettingsModal
      opened={settingsOpen} payload={featureSettings} preferredStrategies={relevantStrategies}
      onClose={() => setSettingsOpen(false)} onSave={saveFeatureSettings} />}
    {section === "features" && !featureSettings && <Modal opened={settingsOpen}
      onClose={() => setSettingsOpen(false)} title="Feature settings">
      <Alert color="red">Feature settings are unavailable.</Alert>
    </Modal>}
    {section !== "features" && <Modal opened={settingsOpen} onClose={() => setSettingsOpen(false)}
      title={`${titles[section]} settings`}>
      <Stack><Text size="sm" c="dimmed">These settings are global and can be updated by any authenticated user.</Text>
        {Object.entries(globalSettings[section] || {}).map(([key, original]) => <NumberInput key={key}
          label={settingLabels[key] || key.replace(/_/g, " ")}
          min={0} max={key.startsWith("size_") ? undefined : 100}
          value={draftSettings[key] ?? original} onChange={(value) => editDraftSetting(key, value)} />)}
        {(section === "static_live" || section === "static_dynamic") && <Select
          label="Split By" value={draftSplitBy} allowDeselect={false}
          description="Create separate aggregate and race-day metric tables for each group."
          data={splitByOptions}
          onChange={(value) => {
            if (value && splitByOptions.some((option) => option.value === value)) {
              setDraftSplitBy(value as SplitBy);
            }
          }} />}
        {settingsError && <Text size="sm" c="red">{settingsError}</Text>}
        <Group justify="flex-end"><Button variant="light" onClick={() => setSettingsOpen(false)}>Cancel</Button>
          <Button onClick={saveGlobal} disabled={!!settingsError}>Save</Button></Group>
      </Stack>
    </Modal>}
  </Stack>;
}

function FeatureTable({ cells, raceLabel }: { cells: Cell[]; raceLabel?: string }) {
  const horses = Array.from(new Set(cells.map((cell) => cell.horse_id)));
  const priority: Record<Status, number> = { red: 0, yellow: 1, green: 2, grey: 3 };
  const rowPriority = new Map<string, number>();
  cells.forEach((cell) => rowPriority.set(cell.name, Math.min(
    rowPriority.get(cell.name) ?? 4, priority[cell.status]
  )));
  const names = Array.from(rowPriority.keys())
    .sort((a, b) => (rowPriority.get(a) ?? 4) - (rowPriority.get(b) ?? 4)
      || a.localeCompare(b));
  const renderValue = (cell: Cell | undefined, name: string) => <>
    <StatusBadge status={cell?.status || "grey"} reason={cell?.reason} />
    {(name !== "ResolvedHorseID" || cell?.status !== "green") && <>
      <Text size="xs">Static {display(cell?.static)}</Text><Text size="xs">Live {display(cell?.dynamic_live)}</Text>
    </>}
    {cell?.implied_probability_diff_pp != null && <Text size="xs" c="dimmed">
      Implied probability difference {display(cell.implied_probability_diff_pp)} pp
    </Text>}
  </>;
  const findCell = (name: string, horse: string) => cells.find((entry) => entry.horse_id === horse && entry.name === name);
  const headerCellStyle = { background: "var(--mantine-color-body)" };
  return <Box style={{ maxHeight: "70vh", overflow: "auto" }}><Table withTableBorder striped stickyHeader
    style={{ borderCollapse: "separate", borderSpacing: 0 }}>
    <Table.Thead>{raceLabel && <Table.Tr><Table.Th colSpan={horses.length + 1} style={headerCellStyle}>{raceLabel}</Table.Th></Table.Tr>}
    <Table.Tr><Table.Th style={headerCellStyle}>Feature</Table.Th>
    {horses.map((horse) => <Table.Th key={horse} style={headerCellStyle}>{horse}</Table.Th>)}
    </Table.Tr>
  </Table.Thead><Table.Tbody>{names.map((name) => <Table.Tr key={name}><Table.Td>{name}</Table.Td>
    {horses.map((horse) => <Table.Td key={horse}>{renderValue(findCell(name, horse), name)}</Table.Td>)}
  </Table.Tr>)}</Table.Tbody></Table></Box>;
}

function PredictionTable({ cells }: { cells: Cell[] }) {
  return <ScrollArea><Table striped withTableBorder><Table.Thead><Table.Tr>
    <Table.Th>Runner</Table.Th><Table.Th>Static backtest</Table.Th><Table.Th>Dynamic backtest</Table.Th><Table.Th>Dynamic live</Table.Th>
  </Table.Tr></Table.Thead><Table.Tbody>{cells.map((cell) => <Table.Tr key={cell.horse_id}>
    <Table.Td>{cell.horse_id}<Box><StatusBadge status={cell.status} reason={cell.reason} /></Box></Table.Td>
    <Table.Td>{prediction(cell.static)}</Table.Td><Table.Td>{prediction(cell.dynamic_backtest)}</Table.Td>
    <Table.Td>{prediction(cell.dynamic_live)}</Table.Td>
  </Table.Tr>)}</Table.Tbody></Table></ScrollArea>;
}

function RaceDetailAccordion({ races, cells, section, showMeeting }: {
  races: Race[]; cells: Cell[]; section: Section; showMeeting: boolean;
}) {
  const firstRace = races[0]?.race_id;
  return <Accordion multiple defaultValue={firstRace ? [firstRace] : []} variant="separated">
    {races.map((race) => {
      const raceCells = cells.filter((cell) => cell.race_id === race.race_id);
      const raceTime = race.starts_at ? new Intl.DateTimeFormat("en-GB", {
        timeZone: "Europe/Dublin", hour: "2-digit", minute: "2-digit",
      }).format(new Date(race.starts_at)) : null;
      const raceLabel = [showMeeting ? race.meeting : null, race.race_id, raceTime]
        .filter(Boolean).join(" · ");
      return <Accordion.Item key={race.race_id} value={race.race_id}>
        <Accordion.Control>
          <Group justify="space-between" wrap="nowrap" pr="sm">
            <Text fw={600}>{raceLabel}</Text>
            <StatusBadge status={race.status} reason={race.reason} />
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          {raceCells.length
            ? section === "features" ? <FeatureTable cells={raceCells} />
              : <PredictionTable cells={raceCells} />
            : <Text size="sm" c="dimmed">No paired runner data for this race.</Text>}
        </Accordion.Panel>
      </Accordion.Item>;
    })}
  </Accordion>;
}
